import { Button } from '@components/Buttons/Base'
import { QUANTITY } from '@features/needs/infinite/constants'
import type { NeedsListPage } from '@features/needs/infinite/types'
import { getPriceWithTip } from '@features/needs/model/Tips'
import { isNeedMet } from '@purposity/utils'
import * as Colors from '@themes/Colors'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import { StyleSheet, View, type ViewProps } from 'react-native'
import { EmptyList } from '~/Components/Fillers/EmptyList'
import { NeedCardView, type NeedCardViewProps } from './NeedCard'
import { NeedsListLoader } from './NeedsListLoader'

type Props = {
  isLoading: boolean
  isError: boolean
  pages: NeedsListPage[] | undefined
  hasNextPage: boolean | undefined
  fetchNextPage: any
  isFetchingNextPage: boolean
  /** Props to be applied on the container for the entire list */
  containerProps?: ViewProps
  /** Props to be applied on *every* `NeedCard` in the list */
  itemProps?: Partial<NeedCardViewProps>
}

export function NeedsListView(props: Props) {
  if (props.isLoading || props.isError) {
    return (
      <View style={styles.container}>
        <NeedsListLoader length={QUANTITY} />
      </View>
    )
  }

  const entryCount =
    props.pages?.reduce(
      (acc, page) => acc + page.needs_connection.edges.length,
      0
    ) ?? 0

  if (!props.pages || !entryCount) {
    return (
      <View style={styles.container}>
        <EmptyList />
      </View>
    )
  }

  return (
    <View
      {...props.containerProps}
      style={[styles.container, props.containerProps?.style]}
    >
      {props.pages.map((page) => (
        <React.Fragment
          key={`page_${page.needs_connection.pageInfo.endCursor}`}
        >
          {page.needs_connection.edges.map(
            ({ cursor, node: { organization, ...need } }) => (
              <NeedCardView
                key={cursor}
                organizationName={organization?.name || ''}
                organizationProfileImagePath={
                  organization?.profile_image_path || '/fallback'
                }
                title={need.title || ''}
                shortDescription={need.short_description || ''}
                priceIncludingTip={getPriceWithTip(
                  need.price_including_sales_tax,
                  need.tax_exempt ?? false
                )}
                city={need.location?.city || undefined}
                state={need.location?.state || undefined}
                containerProps={{
                  ...props.itemProps?.containerProps,
                  style: [
                    styles.itemContainer,
                    props.itemProps?.containerProps?.style,
                  ],
                  nativeID: `need-card-${need.id}`,
                }}
                hasThankYou={Boolean(
                  need.thank_yous_aggregate.aggregate?.count
                )}
                userMetThisNeed={need.is_met_by_current_user || false}
                isMet={isNeedMet(need)}
                urgent={need.urgent || false}
                {...props.itemProps}
                status="success"
                id={need.id}
                picture_path={need.need_information?.picture_path}
              />
            )
          )}
        </React.Fragment>
      ))}

      {props.hasNextPage && (
        <>
          <Button
            style={{ margin: 18 }}
            isBusy={props.isFetchingNextPage}
            onClick={() => props.fetchNextPage()}
            format="rounded"
            title="Load More"
          />
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    flexShrink: 0,
    paddingTop: 12,
    backgroundColor: Colors.white,
  },
  itemContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
})

export function LoadMoreDetector(props: {
  action: () => void
  isLoading: boolean
}) {
  const { ref, inView } = useInView({
    threshold: 1,
    initialInView: false,
    delay: 50,
  })

  React.useEffect(() => {
    if (inView) {
      props.action()
    }
  }, [inView, props])

  return <div ref={ref} style={{ padding: 1 }} />
}
